/**
 * Страница настроек системы
 * Управление данными и системные настройки
 */

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  RefreshCw,
  Database,
  AlertCircle,
  CheckCircle,
  X,
  HardDrive,
  Clock,
  Info
} from 'lucide-react'
import { logAction, ActionTypes, cleanupOldLogs } from '../utils/auditLogger'
import { dataService } from '../../services/dataService'

// Компонент секции настроек
const SettingsSection = ({ title, description, children, icon: Icon }) => {
  return (
    <div className="bg-white border rounded-lg p-6">
      <div className="flex items-center space-x-3 mb-4">
        <Icon className="w-6 h-6 text-blue-600" />
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

// Компонент статистики базы данных
const DatabaseStats = () => {
  const [stats, setStats] = useState({
    departments: 0,
    positions: 0,
    transitions: 0,
    lastUpdate: null
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let isMounted = true
    
    const loadStats = async () => {
      try {
        await dataService.waitForInitialization()
        
        if (!isMounted) return

        const departments = await dataService.getDepartments()
        const positions = await dataService.getPositions()
        const transitions = await dataService.getTransitions()
        const dbStats = {
          departments: departments.length,
          positions: positions.length,
          transitions: transitions.length,
          lastUpdate: Date.now()
        }
        
        if (!isMounted) return
        
        setStats(dbStats)
      } catch (error) {
        console.error('Ошибка загрузки статистики:', error)
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadStats()
    
    return () => {
      isMounted = false
    }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <RefreshCw className="w-4 h-4 animate-spin" />
        <span>Загрузка статистики...</span>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <div className="bg-blue-50 p-3 rounded-lg">
        <div className="text-2xl font-bold text-blue-600">{stats.departments}</div>
        <div className="text-sm text-blue-700">Отделов</div>
      </div>
      <div className="bg-green-50 p-3 rounded-lg">
        <div className="text-2xl font-bold text-green-600">{stats.positions}</div>
        <div className="text-sm text-green-700">Должностей</div>
      </div>
      <div className="bg-purple-50 p-3 rounded-lg">
        <div className="text-2xl font-bold text-purple-600">{stats.transitions}</div>
        <div className="text-sm text-purple-700">Переходов</div>
      </div>
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="text-sm font-medium text-gray-600">
          {stats.lastUpdate ? new Date(stats.lastUpdate).toLocaleString('ru') : 'Нет данных'}
        </div>
        <div className="text-sm text-gray-700">Обновлено</div>
      </div>
    </div>
  )
}

// Основной компонент страницы
const SettingsPage = () => {
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState({ type: null, text: '' })

  // Показ сообщения
  const showMessage = (type, text) => {
    setMessage({ type, text })
    setTimeout(() => setMessage({ type: null, text: '' }), 5000)
  }

  // Перезагрузка данных из серверного API
  const handleReloadData = async () => {
    if (!window.confirm('Перезагрузить данные из серверного API?\n\nЭто очистит кэш и загрузит актуальные данные.')) {
      return
    }

    try {
      setLoading(true)
      
      // Очистка кэша и перезагрузка
      dataService.clearCache()
      await dataService.init()
      
      // Получаем актуальные данные
      const departments = await dataService.getDepartments()
      const positions = await dataService.getPositions()
      const transitions = await dataService.getTransitions()
      
      showMessage('success', `✅ Данные перезагружены: ${departments.length} отделов, ${positions.length} должностей, ${transitions.length} переходов`)
      
      // Логируем действие
      logAction(ActionTypes.DATA_IMPORTED, {
        source: 'server_api',
        departments: departments.length,
        positions: positions.length,
        transitions: transitions.length
      })
      
    } catch (error) {
      console.error('Ошибка перезагрузки:', error)
      showMessage('error', `❌ Ошибка загрузки: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  // Очистка кэша
  const handleClearCache = () => {
    if (window.confirm('Очистить кэш?\n\nПриложение перезагрузится для применения изменений.')) {
      dataService.clearCache()
      showMessage('success', 'Кэш очищен. Перезагружаю страницу...')
      setTimeout(() => window.location.reload(), 1500)
    }
  }

  // Очистка логов
  const handleCleanupLogs = () => {
    if (!window.confirm('Удалить логи старше 90 дней?')) {
      return
    }

    try {
      const removedCount = cleanupOldLogs(90)
      if (removedCount > 0) {
        showMessage('success', `Удалено ${removedCount} старых записей из логов`)
      } else {
        showMessage('info', 'Нет логов для удаления')
      }
    } catch (error) {
      console.error('Ошибка очистки логов:', error)
      showMessage('error', 'Ошибка очистки логов')
    }
  }

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Настройки системы</h1>
        <p className="text-gray-600">Управление данными и системные настройки</p>
      </div>

      {/* Сообщения */}
      <AnimatePresence>
        {message.type && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`p-4 rounded-lg flex items-center space-x-2 ${
              message.type === 'success' ? 'bg-green-50 border border-green-200' :
              message.type === 'error' ? 'bg-red-50 border border-red-200' :
              'bg-blue-50 border border-blue-200'
            }`}
          >
            {message.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-600" /> :
             message.type === 'error' ? <AlertCircle className="w-5 h-5 text-red-600" /> :
             <Info className="w-5 h-5 text-blue-600" />}
            <span className={
              message.type === 'success' ? 'text-green-700' :
              message.type === 'error' ? 'text-red-700' :
              'text-blue-700'
            }>
              {message.text}
            </span>
            <button
              onClick={() => setMessage({ type: null, text: '' })}
              className="ml-auto text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Статистика базы данных */}
      <SettingsSection
        title="Статистика базы данных"
        description="Информация о текущем состоянии данных"
        icon={Database}
      >
        <DatabaseStats />
      </SettingsSection>

      {/* Управление данными */}
      <SettingsSection
        title="Управление данными"
        description="Перезагрузка и синхронизация с серверным API"
        icon={RefreshCw}
      >
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              📊 Система работает с серверным API. Данные загружаются автоматически при запуске.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleReloadData}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              <span>Перезагрузить данные</span>
            </button>
            
            <button
              onClick={handleClearCache}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
            >
              <HardDrive className="w-4 h-4" />
              <span>Очистить кэш</span>
            </button>
          </div>
        </div>
      </SettingsSection>

      {/* Системное обслуживание */}
      <SettingsSection
        title="Системное обслуживание"
        description="Очистка логов и оптимизация системы"
        icon={HardDrive}
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Очистка логов аудита</h4>
            <p className="text-sm text-gray-600 mb-3">
              Удалить записи аудита старше 90 дней для экономии места
            </p>
            <button
              onClick={handleCleanupLogs}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center space-x-2"
            >
              <Clock className="w-4 h-4" />
              <span>Очистить старые логи</span>
            </button>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <div className="flex items-start space-x-2">
              <Info className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800">Статус системы</p>
                <p className="text-yellow-700">
                  Версия: 2.0 | База данных: Серверный API | 
                  Статус: {dataService.isInitialized ? 'Активно' : 'Ошибка'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </SettingsSection>
    </div>
  )
}

export default SettingsPage 